import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';
import { Ref } from 'react';

import { FormattedMessage } from '@flipster/i18n';
import { createTabsComponent } from '@flipster/react/design-system/tabs';
import { NavigationHeightVariableKey } from '@flipster/react/panda/globals/vars';
import { css } from '@flipster/styled-system/css';
import { flex } from '@flipster/styled-system/patterns';

import { MarketTabHeightVariableKey } from '../constants';
import { marketPageBunja, marketTabListBunja } from '../state';
import { TabValueType } from '../types';
import { MarketTable } from './market-table';
import { DiscoverTradFiCategoryChips } from './tradfi-category-chips';

const Tabs = createTabsComponent<TabValueType>();

interface Props {
  ref?: Ref<HTMLDivElement>;
  anchorRef?: Ref<HTMLDivElement>;
  onChangeTab: (tab: TabValueType) => void;
  scrollToList: () => void;
}

export const MarketDiscover = ({ ref, anchorRef, onChangeTab, scrollToList }: Props) => {
  const { discoverCursorAtom, searchModeAtom } = useBunja(marketPageBunja);
  const isSearchMode = useAtomValue(searchModeAtom);
  const { tab: discoverTab } = useAtomValue(discoverCursorAtom);
  const { tabListAtom } = useBunja(marketTabListBunja);
  const tabList = useAtomValue(tabListAtom);

  return (
    <section className={css({ pb: 48 })}>
      {!isSearchMode && (
        <header
          className={flex({
            zIndex: 3,
            gap: 24,
            p: '0 16px',
            tabletDown: {
              flexDirection: 'column',
            },
            tablet: {
              alignItems: 'center',
              justifyContent: 'space-between',
              p: 0,
            },
          })}
        >
          <h2 className={css({ textStyle: 'Title/L', color: 'content.default.level-5' })}>
            <FormattedMessage id='Common.r250925.Discover' />
          </h2>
        </header>
      )}
      <div
        ref={anchorRef}
        aria-hidden
        style={{
          scrollMarginTop: `calc(var(${MarketTabHeightVariableKey}) + var(${NavigationHeightVariableKey}))`,
        }}
      />
      <div
        className={css({
          position: 'sticky',
          backgroundColor: 'background.default.level-0',
          zIndex: 2,
        })}
        style={{
          top: `calc(var(${NavigationHeightVariableKey}) + var(${MarketTabHeightVariableKey}) - 2px)`,
          scrollMarginTop: `calc(var(${MarketTabHeightVariableKey}) + var(${NavigationHeightVariableKey}))`,
        }}
        ref={ref}
      >
        {!isSearchMode && tabList.length > 0 && (
          <>
            <Tabs type='scrollable' value={discoverTab} onChange={onChangeTab} css={{ pt: { base: 4, tablet: 8 } }}>
              {tabList.map((tab) => (
                <Tabs.Tab key={tab.value} value={tab.value} label={tab.label} />
              ))}
            </Tabs>
            {discoverTab === 'tradfi' && <DiscoverTradFiCategoryChips />}
          </>
        )}
      </div>
      <MarketTable scrollToList={scrollToList} />
    </section>
  );
};
